import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import useStore from "../../../store/useStore";
import toast from "react-hot-toast";
import ShoppingBagOutlinedIcon from "@mui/icons-material/ShoppingBagOutlined";
import PaymentOutlinedIcon from "@mui/icons-material/PaymentOutlined";
import { FaSpinner } from "react-icons/fa";
import {
  openCashfreeCheckout,
  shouldVerifyCashfreePayment,
} from "../../../utils/cashfree";

const STATUS_LABELS = {
  PENDING: "Pending",
  CONFIRMED: "Confirmed",
  PROCESSING: "Processing",
  SHIPPED: "Shipped",
  DELIVERED: "Delivered",
  CANCELLED: "Cancelled",
};

const PAYMENT_METHOD_LABEL = {
  COD: "Cash on Delivery",
  UPI: "UPI",
  CARD: "Card",
  NETBANKING: "Net Banking",
  WALLET: "Wallet",
  ONLINE: "Online",
};

const statusClass = (status) => {
  if (status === "DELIVERED") return "bg-primary-50 text-primary-700";
  if (status === "CANCELLED") return "bg-red-50 text-red-700";
  if (status === "PENDING") return "bg-amber-50 text-amber-700";
  return "bg-gray-100 text-gray-700";
};

const CancelModal = ({ onConfirm, onClose, loading }) => {
  const [remarks, setRemarks] = useState("Changed my mind");

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
      <div className="w-full max-w-sm rounded-lg bg-white p-5">
        <h2 className="text-base font-semibold text-gray-900">Cancel Order</h2>
        <p className="mt-1 text-sm text-gray-500">
          Please tell us why you are cancelling.
        </p>
        <textarea
          rows={3}
          value={remarks}
          onChange={(event) => setRemarks(event.target.value)}
          className="mt-4 w-full rounded-md border border-gray-300 px-3 py-2 text-sm outline-none focus:border-primary-500 focus:ring-1 focus:ring-primary-500"
          placeholder="Reason for cancellation"
          maxLength={200}
        />
        <div className="mt-4 flex gap-3">
          <button
            onClick={onClose}
            className="flex-1 rounded-md border border-gray-300 py-2 text-sm font-semibold text-gray-700 hover:bg-gray-50"
          >
            Keep Order
          </button>
          <button
            onClick={() => onConfirm(remarks)}
            disabled={loading || !remarks.trim()}
            className="flex-1 rounded-md bg-red-600 py-2 text-sm font-semibold text-white hover:bg-red-700 disabled:opacity-60"
          >
            {loading ? (
              <FaSpinner className="mx-auto h-4 w-4 animate-spin" />
            ) : (
              "Cancel"
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

const OrderCard = ({ order, onCancel, onRetryPayment, retrying }) => {
  const navigate = useNavigate();
  const status = (order.status ?? "PENDING").toUpperCase();

  const canCancel = !["CANCELLED", "DELIVERED"].includes(status);
  const canRetry =
    order.payment_method !== "COD" &&
    (order.payment_status ?? "").toUpperCase() === "UNPAID" &&
    !["CANCELLED", "DELIVERED"].includes(status);

  return (
    <div className="rounded-lg border border-gray-200 p-4">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
        <div className="min-w-0">
          <p className="text-xs font-medium uppercase text-gray-500">Order</p>
          <h2 className="mt-1 break-words text-sm font-semibold text-gray-900">
            {order.order_number || `#${order.order_id}`}
          </h2>
        </div>
        <span
          className={`w-fit rounded-full px-2.5 py-1 text-xs font-semibold ${statusClass(status)}`}
        >
          {STATUS_LABELS[status] || status}
        </span>
      </div>

      <div className="mt-4 space-y-2">
        {(order.items || []).slice(0, 3).map((item, index) => (
          <div
            key={index}
            className="flex items-start justify-between gap-3 text-sm"
          >
            <span className="min-w-0 flex-1 break-words text-gray-700">
              {item.name ?? item.product_name ?? item.item_name ?? "Product"}
            </span>
            <span className="shrink-0 text-gray-500">
              x{item.quantity ?? item.qty ?? 1}
            </span>
          </div>
        ))}
        {(order.items || []).length > 3 && (
          <p className="text-xs text-gray-500">
            +{order.items.length - 3} more items
          </p>
        )}
        {(!order.items || order.items.length === 0) && (
          <p className="text-sm text-gray-500">Order details loading</p>
        )}
      </div>

      <div className="mt-4 grid grid-cols-1 gap-2 border-t border-gray-200 pt-4 text-sm sm:grid-cols-3">
        <div>
          <p className="text-xs text-gray-500">Date</p>
          <p className="font-medium text-gray-900">
            {order.created_at
              ? new Date(order.created_at).toLocaleDateString("en-IN", {
                  day: "2-digit",
                  month: "short",
                  year: "numeric",
                })
              : "Not available"}
          </p>
        </div>
        <div>
          <p className="text-xs text-gray-500">Payment</p>
          <p className="font-medium text-gray-900">
            {PAYMENT_METHOD_LABEL[order.payment_method] || order.payment_method}
          </p>
        </div>
        <div>
          <p className="text-xs text-gray-500">Total</p>
          <p className="font-medium text-gray-900">
            Rs {parseFloat(order.total ?? order.total_amount ?? 0).toFixed(2)}
          </p>
        </div>
      </div>

      <div className="mt-4 flex flex-wrap gap-2">
        {canRetry && (
          <button
            onClick={() => onRetryPayment(order.order_id)}
            disabled={retrying}
            className="inline-flex items-center gap-1.5 rounded-md bg-blue-600 px-3 py-2 text-xs font-semibold text-white hover:bg-blue-700 disabled:opacity-60"
          >
            {retrying ? (
              <FaSpinner className="h-3 w-3 animate-spin" />
            ) : (
              <PaymentOutlinedIcon style={{ fontSize: 14 }} />
            )}
            Pay Now
          </button>
        )}
        {canCancel && (
          <button
            onClick={() => onCancel(order.order_id)}
            className="rounded-md border border-red-300 px-3 py-2 text-xs font-semibold text-red-600 hover:bg-red-50"
          >
            Cancel
          </button>
        )}
        <button
          onClick={() => navigate(`/account/track?orderId=${order.order_id}`)}
          className="rounded-md border border-gray-300 px-3 py-2 text-xs font-semibold text-gray-700 hover:bg-gray-50"
        >
          Track
        </button>
      </div>
    </div>
  );
};

const OrdersSection = () => {
  const {
    orders,
    orders_loading,
    orders_error,
    fetchOrders,
    cancelOrder,
    retryPayment,
    verifyPayment,
    setPaymentSession,
  } = useStore();
  const [cancelTarget, setCancelTarget] = useState(null);
  const [cancelling, setCancelling] = useState(false);
  const [retryingId, setRetryingId] = useState(null);

  useEffect(() => {
    fetchOrders();
  }, [fetchOrders]);

  const handleCancelConfirm = async (remarks) => {
    setCancelling(true);
    try {
      await cancelOrder(cancelTarget, remarks);
      toast.success("Order cancelled");
      setCancelTarget(null);
    } catch (error) {
      toast.error(error.message || "Failed to cancel order");
    } finally {
      setCancelling(false);
    }
  };

  const handleRetryPayment = async (orderId) => {
    setRetryingId(orderId);
    try {
      const data = await retryPayment(orderId);
      if (!data?.payment_session_id) {
        throw new Error("No payment session returned");
      }
      setPaymentSession(data.payment_session_id, data.cashfree_order_id);

      const paymentResult = await openCashfreeCheckout({
        paymentSessionId: data.payment_session_id,
        redirectTarget: "_modal",
        returnUrl: `${window.location.origin}/payment/return?order_id=${orderId}`,
      });

      if (paymentResult?.error) {
        throw new Error(
          paymentResult.error.message || "Payment was not completed.",
        );
      }

      if (!shouldVerifyCashfreePayment(paymentResult)) {
        throw new Error("Payment was not completed.");
      }

      const verified = await verifyPayment(orderId);
      const paymentStatus = String(
        verified?.payment_status || "",
      ).toUpperCase();
      if (paymentStatus === "PAID" || paymentStatus === "SUCCESS") {
        toast.success("Payment completed");
        await fetchOrders();
      } else {
        toast.error("Payment is pending or failed");
      }
    } catch (error) {
      toast.error(error.message || "Failed to initiate payment");
    } finally {
      setRetryingId(null);
    }
  };

  return (
    <div>
      <div className="mb-5 flex flex-col gap-3 border-b border-gray-200 pb-4 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-lg font-semibold text-gray-900">My Orders</h1>
          <p className="mt-1 text-sm text-gray-500">
            View current and past orders.
          </p>
        </div>
        <button
          onClick={() => fetchOrders()}
          disabled={orders_loading}
          className="rounded-md border border-gray-300 px-3 py-2 text-sm font-semibold text-gray-700 hover:bg-gray-50 disabled:opacity-50"
        >
          {orders_loading ? (
            <FaSpinner className="h-4 w-4 animate-spin" />
          ) : (
            "Refresh"
          )}
        </button>
      </div>

      {orders_loading && orders.length === 0 && (
        <div className="flex items-center justify-center gap-3 py-12 text-sm text-gray-500">
          <FaSpinner className="h-5 w-5 animate-spin text-primary-600" />
          Loading orders
        </div>
      )}

      {orders_error && (
        <div className="mb-4 rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
          {orders_error}
        </div>
      )}

      {!orders_loading && !orders_error && orders.length === 0 && (
        <div className="flex flex-col items-center justify-center rounded-lg border border-dashed border-gray-200 px-4 py-12 text-center">
          <ShoppingBagOutlinedIcon
            className="text-gray-300"
            style={{ fontSize: 36 }}
          />
          <p className="mt-2 text-sm font-semibold text-gray-700">
            No orders yet
          </p>
          <p className="mt-1 text-sm text-gray-500">
            Your placed orders will appear here.
          </p>
        </div>
      )}

      {orders.length > 0 && (
        <div className="grid grid-cols-1 gap-4 xl:grid-cols-2">
          {orders.map((order) => (
            <OrderCard
              key={order.order_id ?? order.id}
              order={order}
              onCancel={(id) => setCancelTarget(id)}
              onRetryPayment={handleRetryPayment}
              retrying={retryingId === (order.order_id ?? order.id)}
            />
          ))}
        </div>
      )}

      {cancelTarget && (
        <CancelModal
          loading={cancelling}
          onConfirm={handleCancelConfirm}
          onClose={() => setCancelTarget(null)}
        />
      )}
    </div>
  );
};

export default OrdersSection;
